/**
 * AIInsightCardExpandable — single insight card used inside the AI
 * Insights modal (CandidateCardImproved.jsx) and the company-side
 * Resume Details modal (ResumeDetailsModal.jsx).
 *
 * Renders one of the AI-generated narrative fields (Matching Skills,
 * Missing Skills, Strengths, Summary, etc.) as a colored card with an
 * icon header. Long text content is clamped to 3 lines with a "Show
 * more / Show less" toggle.
 *
 * Hidden automatically when content is null / empty / 'null' /
 * 'undefined' / '[]' — keeps consumers from having to write defensive
 * conditionals at every call site.
 *
 * Props:
 *   id        — used only for keys/aria, optional
 *   title     — header text, e.g. "Matching Skills"
 *   content   — the field value. Accepts string OR array OR JSON-string
 *               of an array. Arrays render as badges; strings render as
 *               clamped paragraph text.
 *   variant   — color theme: error | warning | success | info | neutral
 *               (defaults to neutral)
 *   icon      — lucide React icon component (defaults to Info)
 *   expanded  — controlled expanded state from the parent (so the
 *               parent can persist which cards are open across
 *               re-renders)
 *   onToggle  — parent's setter for the expanded flag
 *
 * Extracted from CandidateCardImproved.jsx so the company-side Resume
 * Details modal can render the same card style without duplicating
 * the variant config / expand-toggle logic. Single source of truth.
 */

import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/Components/ui/card';
import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import { Text } from '@/Components/ui/typography';
import { cn } from '@/lib/utils';
import { ChevronUp, ChevronDown, Info } from 'lucide-react';

export default function AIInsightCardExpandable({
  id,            // eslint-disable-line no-unused-vars -- accepted for API symmetry with future card list use
  title,
  content,
  variant = 'neutral',
  icon: Icon,
  expanded,
  onToggle,
}) {
  // Bail early when there's nothing meaningful to render. Catches all
  // the shapes the API has emitted historically: null, undefined, empty
  // string, the literal strings 'null' / 'undefined', or '[]' (empty
  // array serialized as JSON). Without these guards the card renders
  // an empty box.
  if (!content) return null;
  const contentStr = Array.isArray(content) ? JSON.stringify(content) : String(content ?? '');
  if (
    !contentStr ||
    contentStr === 'null' ||
    contentStr === 'undefined' ||
    contentStr.trim() === '' ||
    contentStr === '[]'
  ) {
    return null;
  }

  // Per-variant Tailwind class bundles. Tokens like `error`, `warning`,
  // `success`, `primary` are defined as CSS variables in the project's
  // Tailwind config — same palette the rest of the app uses for status
  // colors, so cards stay visually consistent with the surrounding UI.
  const variantConfig = {
    error: {
      card: 'border-error/30 bg-error-foreground/50 hover:bg-error-foreground/80',
      icon: 'text-error',
      iconBg: 'bg-error-foreground',
      title: 'text-error-text',
      content: 'text-error',
      badge: 'bg-error-foreground text-error border-error/30',
    },
    warning: {
      card: 'border-warning/30 bg-warning-foreground/50 hover:bg-warning-foreground/80',
      icon: 'text-warning',
      iconBg: 'bg-warning-foreground',
      title: 'text-warning-text',
      content: 'text-warning',
      badge: 'bg-warning-foreground text-warning border-warning/30',
    },
    success: {
      card: 'border-success/30 bg-success-foreground/50 hover:bg-success-foreground/80',
      icon: 'text-success',
      iconBg: 'bg-success-foreground',
      title: 'text-success-text',
      content: 'text-success',
      badge: 'bg-success-foreground text-success border-success/30',
    },
    info: {
      card: 'border-primary/30 bg-primary/5 hover:bg-primary/10',
      icon: 'text-primary',
      iconBg: 'bg-primary/10',
      title: 'text-primary',
      content: 'text-primary',
      badge: 'bg-primary/10 text-primary border-primary/30',
    },
    neutral: {
      card: 'border-border bg-muted/50 hover:bg-muted/80',
      icon: 'text-muted-foreground',
      iconBg: 'bg-muted',
      title: 'text-foreground',
      content: 'text-muted-foreground',
      badge: 'bg-muted text-muted-foreground border-border',
    },
  };

  const config = variantConfig[variant] || variantConfig.neutral;

  // The API sometimes hands us already-parsed arrays (new shape) and
  // sometimes JSON-encoded strings (legacy shape). Detect both and
  // normalize to either a string (paragraph) or an array (badges).
  let parsedContent = content;
  let isArray = false;
  if (Array.isArray(content)) {
    parsedContent = content;
    isArray = true;
  } else {
    try {
      const parsed = JSON.parse(contentStr);
      if (Array.isArray(parsed)) {
        parsedContent = parsed;
        isArray = true;
      }
    } catch {
      parsedContent = contentStr;
    }
  }

  const DefaultIcon = Icon || Info;
  const contentText =
    typeof parsedContent === 'string'
      ? parsedContent
      : isArray
      ? parsedContent.join(', ')
      : JSON.stringify(parsedContent);

  // Only paragraph (non-array) content with substantial length earns the
  // expand toggle. 100 chars ≈ 3 short lines at the card font size, so
  // anything shorter already fits without clamping.
  const shouldShowExpand = !isArray && contentText.length > 100;

  return (
    <Card className={cn('transition-all duration-200', config.card)}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className={cn('p-1.5 rounded-md', config.iconBg)}>
            <DefaultIcon className={cn('w-3.5 h-3.5', config.icon)} />
          </div>
          <CardTitle className={cn('text-xs font-semibold flex-1', config.title)}>
            {title}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isArray && Array.isArray(parsedContent) ? (
          /* Array → render each item as a Badge. Wraps freely so card
             height grows with the count. */
          <div className="flex flex-wrap gap-1">
            {parsedContent.map((item, index) => (
              <Badge
                key={index}
                variant="outline"
                className={cn('text-[10px] font-normal py-0.5 px-1.5', config.badge)}
              >
                {item}
              </Badge>
            ))}
          </div>
        ) : (
          /* String → paragraph with optional 3-line clamp + expand. */
          <div>
            <Text
              size="xs"
              className={cn(
                'leading-relaxed',
                config.content,
                !expanded && shouldShowExpand && 'line-clamp-3'
              )}
            >
              {contentText}
            </Text>
            {shouldShowExpand && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 mt-1 text-xs"
                onClick={onToggle}
              >
                {expanded ? (
                  <>
                    Show less <ChevronUp className="w-3 h-3 ml-1" />
                  </>
                ) : (
                  <>
                    Show more <ChevronDown className="w-3 h-3 ml-1" />
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
